import { create } from 'zustand';
import { FeatureFlagEnum } from '../../featureflag/feature-flag.enum';
import { systemParametersRepository } from '../../featureflag/system-parameters.repository';
import { SystemParameter, SystemParametersStore } from './systemParameters.types';

const useSystemParametersStore = create<SystemParametersStore>((set, get) => ({
  brandSystemParameters: [],
  globalSystemParameters: [],
  storeSystemParameters: [],
  storeBrandsAggregatedSystemParameters: [],

  fetchBrandSystemParameters: async () => {
    const response = await systemParametersRepository.getBrand();
    if (response.ok) {
      const brandSystemParameters = response?.data as SystemParameter[];
      set({
        brandSystemParameters,
      });
    }
  },

  fetchGlobalSystemParameters: async () => {
    const response = await systemParametersRepository.getGlobal();
    if (response.ok) {
      const globalSystemParameters = response?.data as SystemParameter[];
      set({
        globalSystemParameters,
      });
    }
  },

  fetchStoreSystemParameters: async () => {
    const response = await systemParametersRepository.getStore();
    if (response.ok) {
      const storeSystemParameters = response?.data as SystemParameter[];
      set({
        storeSystemParameters,
      });
    }
  },

  fetchStoreBrandsAggregatedSystemParameters: async () => {
    const response = await systemParametersRepository.getStoreBrandsAggregated();
    if (response.ok) {
      const storeBrandsAggregatedSystemParameters = response?.data as SystemParameter[];
      set({
        storeBrandsAggregatedSystemParameters,
      });
    }
  },

  clearBrandSystemParameters: () => set({ brandSystemParameters: [] }),
  clearStoreBrandsAggregatedSystemParameters: () =>
    set({ storeBrandsAggregatedSystemParameters: [] }),

  getSystemParameterActive: (featureFlag: FeatureFlagEnum, defaultValue = false): boolean => {
    const {
      storeSystemParameters,
      brandSystemParameters,
      storeBrandsAggregatedSystemParameters,
      globalSystemParameters,
    } = get();

    const storeSystemParameter = storeSystemParameters?.find((item) => item.name === featureFlag);
    if (storeSystemParameter) {
      return storeSystemParameter?.value === true;
    }

    const brandSystemParameter = brandSystemParameters?.find((item) => item.name === featureFlag);
    if (brandSystemParameter) {
      return brandSystemParameter?.value === true;
    }

    const isStoreContext = brandSystemParameters.length === 0;
    const aggregatedSystemParameter = isStoreContext
      ? storeBrandsAggregatedSystemParameters?.find((item) => item.name === featureFlag)
      : undefined;

    if (aggregatedSystemParameter) {
      return aggregatedSystemParameter.value === true;
    }

    const globalSystemParameter = globalSystemParameters?.find((item) => item.name === featureFlag);
    if (globalSystemParameter) {
      return globalSystemParameter?.value === true;
    }

    return defaultValue === true;
  },

  getBrandSystemParameterActive: (featureFlag: FeatureFlagEnum, defaultValue = false): boolean => {
    const { brandSystemParameters } = get();

    const brandSystemParameter = brandSystemParameters?.find((item) => item.name === featureFlag);
    if (brandSystemParameter) {
      return brandSystemParameter.value === true;
    }

    return defaultValue;
  },

  getSystemParameterValue: <T>(featureFlag: FeatureFlagEnum, defaultValue?: T): T | undefined => {
    const { brandSystemParameters } = get();

    const brandSystemParameter = brandSystemParameters?.find((item) => item.name === featureFlag);
    if (brandSystemParameter) {
      return brandSystemParameter.value as T;
    }

    return defaultValue;
  },

  getGlobalSystemParameterValue: <T>(
    featureFlag: FeatureFlagEnum,
    defaultValue?: T,
  ): T | undefined => {
    const { globalSystemParameters } = get();

    const brandSystemParameter = globalSystemParameters?.find((item) => item.name === featureFlag);
    if (brandSystemParameter) {
      return brandSystemParameter.value as T;
    }

    return defaultValue;
  },

  getBrandSystemParameterValue: <T>(featureFlag: FeatureFlagEnum): T | undefined => {
    const { brandSystemParameters } = get();

    const brandSystemParameter = brandSystemParameters?.find((item) => item.name === featureFlag);
    if (brandSystemParameter) {
      return brandSystemParameter.value as T;
    }
  },
}));

export default useSystemParametersStore;
